/**
 * Progress Tracking Mixin
 * Handles athlete progress data fetching, calculation, and visualization
 */
import $backend from '../backend'

export default {
  data () {
    return {
      // Progress data
      progressData: null,
      progressLoading: false,
      progressError: null,

      // Timeframe selection
      selectedTimeframe: 'last_30_days', // last_7_days, last_30_days, last_90_days, custom, event
      customStartDate: null,
      customEndDate: null,
      selectedEvent: null,

      // Progress metrics
      progressMetrics: {
        totalActivities: 0,
        totalDistance: 0,
        totalTime: 0,
        totalElevation: 0,
        averagePace: 0,
        averageSpeed: 0,
        averageHeartRate: 0,
        longestRun: 0,
        longestRide: 0
      },

      // Activity breakdown
      activityBreakdown: {
        byType: {},
        byWeek: [],
        byDay: []
      },

      // Comparison data (current period vs previous period)
      comparisonData: {
        distanceChange: 0,
        timeChange: 0,
        activitiesChange: 0,
        paceImprovement: 0
      }
    }
  },

  computed: {
    /**
     * Get date range based on selected timeframe
     * @returns {Object} { startDate, endDate }
     */
    dateRange () {
      const now = new Date()
      let startDate, endDate

      switch (this.selectedTimeframe) {
        case 'last_7_days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          endDate = now
          break

        case 'last_30_days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          endDate = now
          break

        case 'last_90_days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
          endDate = now
          break

        case 'custom':
          startDate = this.customStartDate ? new Date(this.customStartDate) : new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          endDate = this.customEndDate ? new Date(this.customEndDate) : now
          break

        case 'event':
          if (this.selectedEvent) {
            startDate = new Date(this.selectedEvent.from * 1000)
            endDate = new Date(this.selectedEvent.to * 1000)
          } else {
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            endDate = now
          }
          break

        default:
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          endDate = now
      }

      return { startDate, endDate }
    },

    /**
     * Calculate progress percentage (if event-based)
     * @returns {number} Progress percentage (0-100)
     */
    eventProgress () {
      if (!this.selectedEvent) return 0

      const now = Date.now()
      const start = this.selectedEvent.from * 1000
      const end = this.selectedEvent.to * 1000

      if (now < start) return 0
      if (now > end) return 100

      const total = end - start
      const elapsed = now - start
      return Math.round((elapsed / total) * 100)
    },

    /**
     * Days remaining in event
     * @returns {number} Days remaining
     */
    daysRemaining () {
      if (!this.selectedEvent) return 0

      const now = Date.now()
      const end = this.selectedEvent.to * 1000

      if (now > end) return 0

      const msRemaining = end - now
      return Math.ceil(msRemaining / (24 * 60 * 60 * 1000))
    }
  },

  methods: {
    /**
     * Load progress data for the selected timeframe
     */
    async loadProgressData () {
      if (!this.athlete || !this.athlete._id) {
        this.progressError = this.$t('profile.athleteNotFound')
        return
      }

      this.progressLoading = true
      this.progressError = null

      try {
        const { startDate, endDate } = this.dateRange

        // Fetch activities within date range
        const activities = await this.fetchActivitiesInRange(startDate, endDate)

        // Calculate metrics
        this.calculateProgressMetrics(activities)

        // Calculate breakdown
        this.calculateActivityBreakdown(activities)

        // Calculate comparison (current vs previous period)
        await this.calculateComparison(startDate, endDate, activities.length)

        this.progressData = {
          activities,
          dateRange: { startDate, endDate },
          metrics: this.progressMetrics,
          breakdown: this.activityBreakdown,
          comparison: this.comparisonData
        }
      } catch (error) {
        console.error('Error loading progress data:', error)
        this.progressError = this.getErrorMessage(error)
      } finally {
        this.progressLoading = false
      }
    },

    /**
     * Fetch activities within a date range
     * @param {Date} startDate - Start date
     * @param {Date} endDate - End date
     * @returns {Promise<Array>} Array of activities
     */
    async fetchActivitiesInRange (startDate, endDate) {
      // Convert dates to Unix timestamps (seconds)
      const fromTs = Math.floor(startDate.getTime() / 1000)
      const toTs = Math.floor(endDate.getTime() / 1000)

      // Fetch all activities in range (use large limit to get all)
      const response = await $backend.getAthleteActivities(
        this.athlete._id,
        this.athlete.token.access_token,
        0, // offset
        10000, // large limit to get all activities in range
        fromTs,
        toTs
      )

      return response.data || []
    },

    /**
     * Calculate progress metrics from activities
     * @param {Array} activities - Array of activities
     */
    calculateProgressMetrics (activities) {
      if (!activities || activities.length === 0) {
        this.progressMetrics = {
          totalActivities: 0,
          totalDistance: 0,
          totalTime: 0,
          totalElevation: 0,
          averagePace: 0,
          averageSpeed: 0,
          averageHeartRate: 0,
          longestRun: 0,
          longestRide: 0
        }
        return
      }

      let totalDistance = 0
      let totalTime = 0
      let totalElevation = 0
      let totalHeartRate = 0
      let heartRateCount = 0
      let longestRun = 0
      let longestRide = 0
      let runningActivities = []

      activities.forEach(activity => {
        const distance = activity.distance || 0
        const time = activity.moving_time || 0
        const elevation = activity.total_elevation_gain || 0

        totalDistance += distance
        totalTime += time
        totalElevation += elevation

        if (activity.average_heartrate) {
          totalHeartRate += activity.average_heartrate
          heartRateCount++
        }

        // Track longest activities
        if (activity.type === 'Run' || activity.type === 'VirtualRun') {
          if (distance > longestRun) longestRun = distance
          runningActivities.push({ distance, time })
        } else if (activity.type === 'Ride' || activity.type === 'VirtualRide') {
          if (distance > longestRide) longestRide = distance
        }
      })

      // Calculate average pace for running activities (min/km)
      let averagePace = 0
      if (runningActivities.length > 0) {
        const totalRunDistance = runningActivities.reduce((sum, act) => sum + (act.distance / 1000), 0)
        const totalRunTime = runningActivities.reduce((sum, act) => sum + (act.time / 60), 0)
        if (totalRunDistance > 0) {
          averagePace = totalRunTime / totalRunDistance
        }
      }

      // Calculate average speed (km/h)
      const averageSpeed = totalTime > 0 ? (totalDistance / 1000) / (totalTime / 3600) : 0

      this.progressMetrics = {
        totalActivities: activities.length,
        totalDistance: Math.round(totalDistance / 1000), // Convert to km
        totalTime: Math.round(totalTime / 60), // Convert to minutes
        totalElevation: Math.round(totalElevation),
        averagePace: Math.round(averagePace * 100) / 100,
        averageSpeed: Math.round(averageSpeed * 100) / 100,
        averageHeartRate: heartRateCount > 0 ? Math.round(totalHeartRate / heartRateCount) : 0,
        longestRun: Math.round(longestRun / 1000), // Convert to km
        longestRide: Math.round(longestRide / 1000) // Convert to km
      }
    },

    /**
     * Calculate activity breakdown by type, week, and day
     * @param {Array} activities - Array of activities
     */
    calculateActivityBreakdown (activities) {
      const byType = {}
      const byWeek = {}
      const byDay = {}

      activities.forEach(activity => {
        const type = activity.type || 'Unknown'
        const date = new Date(activity.start_date_local)
        const weekKey = this.getWeekKey(date)
        const dayKey = this.getDayKey(date)

        // By type
        if (!byType[type]) {
          byType[type] = {
            count: 0,
            distance: 0,
            time: 0,
            elevation: 0
          }
        }
        byType[type].count++
        byType[type].distance += activity.distance || 0
        byType[type].time += activity.moving_time || 0
        byType[type].elevation += activity.total_elevation_gain || 0

        // By week
        if (!byWeek[weekKey]) {
          byWeek[weekKey] = {
            week: weekKey,
            count: 0,
            distance: 0,
            time: 0
          }
        }
        byWeek[weekKey].count++
        byWeek[weekKey].distance += activity.distance || 0
        byWeek[weekKey].time += activity.moving_time || 0

        // By day
        if (!byDay[dayKey]) {
          byDay[dayKey] = {
            day: dayKey,
            count: 0,
            distance: 0,
            time: 0
          }
        }
        byDay[dayKey].count++
        byDay[dayKey].distance += activity.distance || 0
        byDay[dayKey].time += activity.moving_time || 0
      })

      // Convert to arrays and sort
      this.activityBreakdown = {
        byType: Object.entries(byType).map(([type, item]) => ({
          type,
          ...item,
          distance: Math.round(item.distance / 1000),
          time: Math.round(item.time / 60),
          elevation: Math.round(item.elevation)
        })),
        byWeek: Object.values(byWeek)
          .map(item => ({
            ...item,
            distance: Math.round(item.distance / 1000),
            time: Math.round(item.time / 60)
          }))
          .sort((a, b) => a.week.localeCompare(b.week)),
        byDay: Object.values(byDay)
          .map(item => ({
            ...item,
            distance: Math.round(item.distance / 1000),
            time: Math.round(item.time / 60)
          }))
          .sort((a, b) => a.day.localeCompare(b.day))
      }
    },

    /**
     * Calculate comparison with previous period
     * @param {Date} startDate - Current period start
     * @param {Date} endDate - Current period end
     * @param {number} currentCount - Current activities count
     */
    async calculateComparison (startDate, endDate, currentCount) {
      try {
        const periodLength = endDate - startDate
        const previousStart = new Date(startDate.getTime() - periodLength)
        const previousEnd = startDate

        const previousActivities = await this.fetchActivitiesInRange(previousStart, previousEnd)

        if (previousActivities.length === 0) {
          this.comparisonData = {
            distanceChange: 0,
            timeChange: 0,
            activitiesChange: currentCount,
            paceImprovement: 0
          }
          return
        }

        // Calculate previous metrics
        const previousMetrics = {
          distance: 0,
          time: 0,
          count: previousActivities.length
        }

        previousActivities.forEach(activity => {
          previousMetrics.distance += activity.distance || 0
          previousMetrics.time += activity.moving_time || 0
        })

        // Calculate changes (percentage)
        const distanceChange = previousMetrics.distance > 0
          ? Math.round(((this.progressMetrics.totalDistance * 1000 - previousMetrics.distance) / previousMetrics.distance) * 100)
          : 0

        const timeChange = previousMetrics.time > 0
          ? Math.round(((this.progressMetrics.totalTime * 60 - previousMetrics.time) / previousMetrics.time) * 100)
          : 0

        const activitiesChange = currentCount - previousMetrics.count

        this.comparisonData = {
          distanceChange,
          timeChange,
          activitiesChange,
          paceImprovement: 0 // Can be calculated if needed
        }
      } catch (error) {
        console.error('Error calculating comparison:', error)
        this.comparisonData = {
          distanceChange: 0,
          timeChange: 0,
          activitiesChange: 0,
          paceImprovement: 0
        }
      }
    },

    /**
     * Set timeframe for progress tracking
     * @param {string} timeframe - Timeframe identifier
     * @param {Object} event - Event object (if timeframe is 'event')
     */
    setTimeframe (timeframe, event = null) {
      this.selectedTimeframe = timeframe

      if (timeframe === 'event' && event) {
        this.selectedEvent = event
      }

      // Reload progress data with new timeframe
      this.loadProgressData()
    },

    /**
     * Set custom date range
     * @param {string} startDate - Start date (ISO string)
     * @param {string} endDate - End date (ISO string)
     */
    setCustomDateRange (startDate, endDate) {
      this.selectedTimeframe = 'custom'
      this.customStartDate = startDate
      this.customEndDate = endDate
      this.loadProgressData()
    },

    /**
     * Get week key for grouping (YYYY-Www)
     * @param {Date} date - Date object
     * @returns {string} Week key
     */
    getWeekKey (date) {
      const year = date.getFullYear()
      const week = this.getWeekNumber(date)
      return `${year}-W${week.toString().padStart(2, '0')}`
    },

    /**
     * Get week number for a date
     * @param {Date} date - Date object
     * @returns {number} Week number (1-53)
     */
    getWeekNumber (date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
    },

    /**
     * Get day key for grouping (YYYY-MM-DD)
     * @param {Date} date - Date object
     * @returns {string} Day key
     */
    getDayKey (date) {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    /**
     * Get error message from response
     * @param {Object} error - Error object
     * @returns {string} Error message
     */
    getErrorMessage (error) {
      if (error.response && error.response.data && error.response.data.msg) {
        return error.response.data.msg
      }
      return this.$t('profile.errorLoadingProgress') || 'Error loading progress data'
    }
  }
}
